import { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

const FAQ_ITEMS = [
  {
    question: 'What is Apex Analyst?',
    answer:
      'Apex Analyst is an AI readiness and visibility analysis platform. You enter any website URL and receive a scored report across six categories — structured data, mobile-friendliness, accessibility, content quality, technical SEO, and privacy compliance — along with prioritized recommendations to make your site discoverable by AI search engines like ChatGPT, Claude, Gemini, and Perplexity.',
  },
  {
    question: 'What is AI readiness and why does it matter?',
    answer:
      'AI readiness is the degree to which a website can be understood and cited by AI-powered answer engines. As users increasingly get answers from ChatGPT, Gemini, and Perplexity instead of traditional search results, sites that are not AI-readable miss out on visibility, traffic, and leads. Apex Analyst quantifies this gap and tells you exactly what to fix.',
  },
  {
    question: 'How does Apex Analyst score my website?',
    answer:
      "The platform fetches your site's HTML, analyzes it across six weighted categories, and returns a score from 0–100. Each category (structured data, mobile, accessibility, content quality, technical SEO, privacy compliance) contributes to an overall AI Readiness Score and a separate AEO (Answer Engine Optimization) score that measures how likely AI tools are to cite your content.",
  },
  {
    question: 'What is structured data and why is it important for AI discoverability?',
    answer:
      'Structured data is machine-readable information embedded in your HTML — typically as JSON-LD using schema.org vocabulary. It tells AI crawlers exactly what your page is about: your business type, products, prices, FAQs, and more. Without it, AI engines must guess at your content, which reduces the chance your site is surfaced in AI-generated answers.',
  },
  {
    question: 'Which AI tools will be able to find my site after I improve my score?',
    answer:
      "Improvements detected by Apex Analyst directly benefit discoverability on ChatGPT (OpenAI's web browsing), Google Gemini, Claude (Anthropic), Perplexity AI, Microsoft Copilot, and traditional search engines like Google and Bing — all of which rely on well-structured, crawlable HTML to understand and cite websites.",
  },
  {
    question: 'How long does a website analysis take?',
    answer:
      'Most analyses complete in 15–30 seconds. The platform fetches your live site, runs all six category checks, and uses AI to generate prioritized recommendations — all in a single request. You receive your full scored report immediately when the analysis finishes.',
  },
  {
    question: 'Is Apex Analyst free to use?',
    answer:
      'Yes. The Free plan includes a complete AI Readiness Score, all six category analyses, and three free analyses so you can evaluate any websites before committing. Paid plans (starting at $9.99/month) unlock performance metrics, AI visibility scores, higher analysis limits, comparison tools, and an AI assistant chat.',
  },
  {
    question: 'What is the difference between SEO and AEO (Answer Engine Optimization)?',
    answer:
      'Traditional SEO focuses on ranking in link-based search results. AEO (Answer Engine Optimization) focuses on getting your content selected as the direct answer in AI-generated responses. AEO requires structured data, clear semantics, authoritative content, and proper schema markup — all areas Apex Analyst specifically measures and scores.',
  },
  {
    question: 'How often should I re-analyze my website?',
    answer:
      'We recommend analyzing your site after every significant content update, technical change, or redesign — and at minimum once per month. AI ranking signals evolve quickly, and ongoing monitoring ensures you catch regressions before they cost you visibility. The Starter plan and above support 25+ analyses per month for continuous monitoring.',
  },
  {
    question: 'How do I get started with Apex Analyst?',
    answer:
      'Simply enter your website URL on the home page and click Analyze. No account is required for your first free analysis. Creating a free account saves your history and unlocks additional analyses. Paid plans are available monthly with no long-term commitment required.',
  },
];

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export function FAQPage() {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set([0]));

  useEffect(() => {
    const descEl = document.querySelector('meta[name="description"]');
    const ogTitleEl = document.querySelector('meta[property="og:title"]');
    const ogDescEl = document.querySelector('meta[property="og:description"]');
    const ogUrlEl = document.querySelector('meta[property="og:url"]');
    const canonicalEl = document.querySelector('link[rel="canonical"]');

    const prev = {
      title: document.title,
      desc: descEl?.getAttribute('content') ?? '',
      ogTitle: ogTitleEl?.getAttribute('content') ?? '',
      ogDesc: ogDescEl?.getAttribute('content') ?? '',
      ogUrl: ogUrlEl?.getAttribute('content') ?? '',
      canonical: canonicalEl?.getAttribute('href') ?? '',
    };

    document.title = 'FAQ — Apex Analyst | AI Readiness & Visibility Questions Answered';
    descEl?.setAttribute('content', 'Answers to the most common questions about Apex Analyst — AI readiness scoring, structured data, AEO vs SEO, pricing, and how to get started.');
    ogTitleEl?.setAttribute('content', 'FAQ — Apex Analyst | AI Readiness & Visibility Questions Answered');
    ogDescEl?.setAttribute('content', "Find answers about Apex Analyst's AI readiness scoring, structured data analysis, pricing plans, and how to make your site discoverable by ChatGPT, Gemini, and Claude.");
    ogUrlEl?.setAttribute('content', 'https://apexanalyst.com/?faq=true');
    canonicalEl?.setAttribute('href', 'https://apexanalyst.com/?faq=true');

    return () => {
      document.title = prev.title;
      descEl?.setAttribute('content', prev.desc);
      ogTitleEl?.setAttribute('content', prev.ogTitle);
      ogDescEl?.setAttribute('content', prev.ogDesc);
      ogUrlEl?.setAttribute('content', prev.ogUrl);
      canonicalEl?.setAttribute('href', prev.canonical);
    };
  }, []);

  const toggleItem = (index: number) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <main className="min-h-screen bg-white py-16 px-4">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_JSON_LD, null, 2) }}
      />

      <div className="container mx-auto max-w-3xl">
        <article>
          <header className="mb-12 text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about Apex Analyst — how we score your website,
              what AI readiness means, and how to make your content discoverable by
              ChatGPT, Claude, Gemini, and Perplexity.
            </p>
          </header>

          <section aria-label="Frequently asked questions">
            <dl className="space-y-3">
              {FAQ_ITEMS.map((item, index) => {
                const isOpen = openItems.has(index);
                return (
                  <div
                    key={index}
                    className={`border rounded-xl transition-colors ${
                      isOpen ? 'border-gray-900 bg-gray-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <dt>
                      <h2 className="m-0 text-base font-semibold leading-none">
                        <button
                          onClick={() => toggleItem(index)}
                          aria-expanded={isOpen}
                          aria-controls={`faq-answer-${index}`}
                          className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-100 transition-colors rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-inset"
                        >
                          <span className="font-semibold text-gray-900 pr-4">
                            {item.question}
                          </span>
                          <ChevronDown
                            className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                      </h2>
                    </dt>
                    <dd
                      id={`faq-answer-${index}`}
                      className="px-6 pb-6 m-0"
                    >
                      <p className={`leading-relaxed transition-colors duration-200 ${isOpen ? 'text-gray-700' : 'text-gray-400'}`}>
                        {item.answer}
                      </p>
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>

          <footer className="mt-16 pt-10 border-t border-gray-200 text-center">
            <p className="text-gray-600 mb-4">
              Still have questions? We're here to help.
            </p>
            <a
              href="mailto:service@round1solutions.com"
              className="inline-flex items-center gap-2 bg-black text-white text-sm font-medium py-3 px-8 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Contact Support
            </a>
          </footer>
        </article>
      </div>
    </main>
  );
}
